import { P2GBox, P2GLink, P2GText } from '@parcel2go/core-ui';
import { ExtraBase, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { getSelectedQuoteOption } from '../generateProtectionKey';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ToExtraTitleTranslationKey } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';

type UnavailableExtrasProps = {
  item: SmartSendItemApiModel;
  onRemove: (extras: ExtraBase[]) => void;
};

const getUnavailableExtrasFromItem = (item: SmartSendItemApiModel): ExtraBase[] => {
  if (!item.State?.validation?.QuoteState?.Quote || !item?.Extras) {
    return [];
  }

  // Get the selected quote option
  const qo = getSelectedQuoteOption(item, item.State.validation.QuoteState.Quote);

  // Possible Extras
  const fullList = qo?.AvailableExtras.concat(qo.Extras) ?? [];

  // Set extras that are no longer available for this service based on the extras on the main Item
  const unavailable = item.Extras.filter((x) => !fullList.find((y) => y.Id === x.Id));

  return unavailable ?? [];
};

const getAvailableExtrasAfterUnavailableHaveBeenRemoved = (item: SmartSendItemApiModel): ExtraBase[] => {
  const unavailableExtraIds = getUnavailableExtrasFromItem(item).map((u) => u.Id);
  return [...item.Extras].filter((x) => !unavailableExtraIds.includes(x.Id));
};

const UnavailableExtras = ({ item, onRemove }: UnavailableExtrasProps) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const unavailableExtras = useMemo(() => getUnavailableExtrasFromItem(item), [item]);
  const onRemoveClick = useCallback(
    () => onRemove(getAvailableExtrasAfterUnavailableHaveBeenRemoved(item)),
    [onRemove, item]
  );

  return (
    <P2GBox sx={{ display: 'flex', flexDirection: 'row', gap: 2, wrap: 'nowrap' }}>
      <P2GBox sx={{ display: 'flex', gap: 1 }}>
        {unavailableExtras.map((e) => (
          <P2GText variant="body2" key={e.Id}>
            <Trans t={t} i18nKey={ToExtraTitleTranslationKey(e)} defaults={e.Name} />
          </P2GText>
        ))}
      </P2GBox>
      <P2GLink onClick={onRemoveClick}>{t('REMOVE_BUTTON', 'Remove')}</P2GLink>
    </P2GBox>
  );
};
export default UnavailableExtras;
